<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  src?: string;
  alt?: string;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  showFrame?: boolean;
  shape?: 'circle' | 'hexagon';
  fallbackIcon?: string;
  fallbackText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  showFrame: true,
  shape: 'circle',
  fallbackIcon: 'bi-person',
  fallbackText: '👤'
});

// Size configurations
const sizeConfig = {
  small: { avatar: { width: 29, height: 34 }, frame: 40, padding: 7 },
  medium: { avatar: { width: 50, height: 55 }, frame: 65, padding: 7 },
  large: { avatar: { width: 70, height: 83 }, frame: 100, padding: 16 },
  xlarge: { avatar: { width: 108, height: 125 }, frame: 150, padding: 23 }
};

const currentSize = computed(() => sizeConfig[props.size]);
</script>

<template>
  <div class="avatar-container" :class="[`avatar-${size}`, { 'with-frame': showFrame }]">
    <!-- Frame image (if enabled) -->
    <img
      v-if="showFrame"
      src="@/images/frame.png"
      alt=""
      class="avatar-frame"
      :style="{
        width: `${currentSize.frame}px`,
        height: `${currentSize.frame}px`
      }"
    />

    <!-- Avatar content -->
    <div
      class="avatar-content"
      :class="`avatar-${shape}`"
      :style="{
        width: `${currentSize.avatar.width}px`,
        height: `${currentSize.avatar.height}px`
      }"
    >
      <img
        v-if="src"
        :src="src"
        :alt="alt || 'Avatar'"
        class="avatar-image"
      />
      <div v-else class="avatar-placeholder">
        <i v-if="fallbackIcon" :class="fallbackIcon"></i>
        <span v-else>{{ fallbackText }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.avatar-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.with-frame {
    .avatar-frame {
      position: relative;
      z-index: 1;
    }

    .avatar-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      overflow: hidden;
      border: none;
      z-index: 2;
    }

    // Special adjustment for xlarge size to fix centering
    &.avatar-xlarge .avatar-content {
      transform: translate(-50%, -50%) translateX(-1px);
    }
  }

  &:not(.with-frame) {
    .avatar-content {
      border-radius: var(--border-radius-none);
      overflow: hidden;
      background-color: var(--gray-200);
      border: 2px solid var(--border-color);
    }
  }

  &.with-frame {
    .avatar-content {
      border: none;
    }
  }
}

.avatar-frame {
  display: block;
  object-fit: contain;
}

.avatar-content {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: none;
  overflow: hidden;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: none;
}

// Shape variations
.avatar-circle {
  border-radius: 0;

  .avatar-image {
    border-radius: 0;
  }
}

.avatar-hexagon {
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);

  .avatar-image {
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  }
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-primary);
  color: var(--dark-base);
  font-size: 1em;

  i {
    font-size: 1.2em;
  }
}

// Size-specific styles
.avatar-small {
  .avatar-placeholder {
    font-size: 0.8em;
  }
}

.avatar-medium {
  .avatar-placeholder {
    font-size: 1em;
  }
}

.avatar-large {
  .avatar-placeholder {
    font-size: 1.5em;
  }
}

.avatar-xlarge {
  .avatar-placeholder {
    font-size: 2em;
  }
}
</style>
